export const DEFAULT_QUICKLOG_TABLE_CODE = 'quicklog';
export const DEFAULT_QUICKLOG_FIELD_CODES = ['rock_type', 'min_style', 'min_type'];
/** @deprecated use table + field codes; kept for callers passing field names */
export const DEFAULT_QUICKLOG_LIST_CODES = DEFAULT_QUICKLOG_FIELD_CODES;

export const FALLBACK_LIST_VISUAL = Object.freeze({
  displayColor: '#9ca3af',
  texture: null,
});

const HASH_PALETTE = [
  '#e6194b',
  '#3cb44b',
  '#ffe119',
  '#4363d8',
  '#f58231',
  '#911eb4',
  '#42d4f4',
  '#f032e6',
  '#469990',
  '#9A6324',
];

const SESSION_CACHE_PREFIX = 'wolfwalker-drillz-list-values-v3';

export function quicklogListVisualsCacheKey(options = {}) {
  const tableCode = String(options.tableCode || DEFAULT_QUICKLOG_TABLE_CODE).trim();
  const fieldCodes = (
    Array.isArray(options.fieldCodes) ? options.fieldCodes : DEFAULT_QUICKLOG_FIELD_CODES
  )
    .map((c) => String(c).trim())
    .filter(Boolean);
  return `${tableCode}:${fieldCodes.join(',')}`;
}

function normalizeLookupKey(value) {
  if (value == null) return '';
  return String(value).trim();
}

/** Same palette as python-api drillz_geometry_mapper._hash_color */
export function hashColorForListValue(text) {
  const s = normalizeLookupKey(text);
  if (!s) return FALLBACK_LIST_VISUAL.displayColor;
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h += s.charCodeAt(i);
  }
  return HASH_PALETTE[h % HASH_PALETTE.length];
}

function visualForListEntry(entry) {
  const key = normalizeLookupKey(entry?.value_key);
  const label = normalizeLookupKey(entry?.value_label);
  const displayColor =
    entry?.displayColor ||
    hashColorForListValue(key || label);

  return {
    displayColor,
    texture: entry?.texture || null,
    value_key: entry?.value_key,
    value_label: entry?.value_label,
  };
}

function indexListValues(values) {
  const byKey = new Map();
  if (!Array.isArray(values)) return byKey;

  values.forEach((entry) => {
    if (!entry) return;
    const visual = visualForListEntry(entry);

    const key = normalizeLookupKey(entry.value_key);
    if (key) {
      byKey.set(key, visual);
      byKey.set(key.toLowerCase(), visual);
    }

    const label = normalizeLookupKey(entry.value_label);
    if (label) {
      byKey.set(label, visual);
      byKey.set(label.toLowerCase(), visual);
    }
  });

  return byKey;
}

/**
 * Build lookup maps from drillz_list_values API response.
 * @returns {Map<string, Map<string, object>>}
 */
export function buildListVisualMaps(apiResponse) {
  const lists = apiResponse?.lists || {};
  const maps = new Map();

  Object.entries(lists).forEach(([listCode, listPayload]) => {
    maps.set(listCode, indexListValues(listPayload?.values));
  });

  return maps;
}

/**
 * Plain object for React props / JSON serialization on hole pages.
 */
export function listVisualMapsToPlainObject(maps) {
  if (!maps || typeof maps !== 'object') return {};
  if (maps instanceof Map) {
    const out = {};
    maps.forEach((valueMap, listCode) => {
      out[listCode] = Object.fromEntries(valueMap.entries());
    });
    return out;
  }
  return maps;
}

/**
 * Rehydrate Map from plain object stored on hole pages.
 */
export function plainObjectToListVisualMaps(plain) {
  if (!plain || typeof plain !== 'object') return new Map();
  const maps = new Map();
  Object.entries(plain).forEach(([listCode, entries]) => {
    maps.set(listCode, new Map(Object.entries(entries || {})));
  });
  return maps;
}

export function resolveListVisual(listMap, rawValue) {
  const map = listMap instanceof Map ? listMap : null;
  const key = normalizeLookupKey(rawValue);
  if (!key) return { ...FALLBACK_LIST_VISUAL };

  if (map) {
    const hit =
      map.get(key) ||
      map.get(key.toLowerCase()) ||
      map.get(key.toUpperCase());
    if (hit) return hit;
  }

  return {
    displayColor: hashColorForListValue(key),
    texture: null,
  };
}

export function swatchStyle(visual, sizePx = 12) {
  const v = visual || FALLBACK_LIST_VISUAL;
  const style = {
    width: `${sizePx}px`,
    height: `${sizePx}px`,
    minWidth: `${sizePx}px`,
    borderRadius: '2px',
    border: '1px solid rgba(0,0,0,0.2)',
    boxSizing: 'border-box',
    backgroundColor: v.displayColor || FALLBACK_LIST_VISUAL.displayColor,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    flexShrink: 0,
  };

  if (v.texture?.dataUrl) {
    style.backgroundImage = `url(${v.texture.dataUrl})`;
  }

  return style;
}

export function normalizeQuicklogIntervals(rows) {
  return (rows || [])
    .map((row) => {
      const from = Number(row.from_depth);
      const to = Number(row.to_depth);
      if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return null;
      return { ...row, from_depth: from, to_depth: to };
    })
    .filter(Boolean)
    .sort((a, b) => a.from_depth - b.from_depth);
}

export function computeQuicklogBarScale(intervals, plannedDepth) {
  const deepestTo = intervals.reduce(
    (max, row) => Math.max(max, row.to_depth),
    0
  );
  const planned = Number(plannedDepth);
  const plannedNum = Number.isFinite(planned) && planned > 0 ? planned : 0;
  const scaleMax = Math.max(deepestTo, plannedNum, 1);

  return { deepestTo, plannedNum, scaleMax };
}

export function getSessionCachedListValues(cacheKey) {
  try {
    const key = `${SESSION_CACHE_PREFIX}:${cacheKey}`;
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.lists) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function setSessionCachedListValues(cacheKey, apiResponse) {
  try {
    const key = `${SESSION_CACHE_PREFIX}:${cacheKey}`;
    sessionStorage.setItem(key, JSON.stringify(apiResponse));
  } catch {
    /* ignore quota */
  }
}

export async function fetchListVisualMapsWithCache(
  fetchFn,
  options = {}
) {
  const normalized =
    typeof options === 'string' || Array.isArray(options)
      ? { fieldCodes: options }
      : options;
  const cacheKey = quicklogListVisualsCacheKey(normalized);
  const cached = getSessionCachedListValues(cacheKey);
  if (cached) {
    return buildListVisualMaps(cached);
  }

  const result = await fetchFn(normalized);
  const hasValues =
    result?.success &&
    result.lists &&
    Object.values(result.lists).some(
      (list) => Array.isArray(list?.values) && list.values.length > 0
    );
  if (hasValues) {
    setSessionCachedListValues(cacheKey, result);
  }
  return buildListVisualMaps(result);
}
